import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { Loading } from '@1trading/react/components/ui/loading';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import LockedAnswersNotice from '@/components/ui/locked-answers-notice';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BUSINESS_SUB_TYPES_BY_BUSINESS_TYPE, BUSINESS_TYPES, BusinessType, LEGAL_FORMS, LegalForm } from '@/constants/institution';
import {
  type InstitutionCompany,
  type InstitutionCompanyOutput,
  institutionCompanySchema,
  institutionCompanyToInstitutionDetailsCompanyApi,
  institutionDetailsCompanyApiToInstitutionCompany,
  institutionDetailsLockedCompanyToInstitutionCompany,
} from '@/features/institution/company/schema';
import { InstitutionFieldsetAddress } from '@/features/institution/components/fieldsets/address';
import { InstitutionFieldsetDate } from '@/features/institution/components/fieldsets/date';
import InstitutionGoToReviewButton from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import {
  useInstitutionDetailsLocked,
  useInstitutionDetailsMutation,
  useInstitutionDetailsPreviousRegisteredAddress,
  useInstitutionDetailsV2Answers,
} from '@/features/institution/hooks/useInstitutionDetails';
import { INSTITUTION_ADDRESS_DEFAULTS } from '@/features/institution/schemas/address';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { EMPTY_DATE_PARTS } from '@/utils/date';

const InstitutionCompanyFormForm = ({ defaultValues, onPrevStep }: { defaultValues: Partial<InstitutionCompany>; onPrevStep: () => void }) => {
  const form = useForm<InstitutionCompany, unknown, InstitutionCompanyOutput>({
    defaultValues: {
      ...defaultValues,
      companyName: defaultValues.companyName ?? '',
      legalFormOther: defaultValues.legalFormOther ?? '',
      registeredAddress: defaultValues.registeredAddress ?? INSTITUTION_ADDRESS_DEFAULTS,
      registrationNumber: defaultValues.registrationNumber ?? '',
      dateOfIncorporation: defaultValues.dateOfIncorporation ?? EMPTY_DATE_PARTS,
      typeOfBusinessOther: defaultValues.typeOfBusinessOther ?? '',
      taxId: defaultValues.taxId ?? '',
      leiNumber: defaultValues.leiNumber ?? '',
      website: defaultValues.website ?? '',
    },
    resolver: standardSchemaResolver(institutionCompanySchema),
  });
  const { isPending, mutate } = useInstitutionDetailsMutation();
  const {
    locked: { company: lockedCompany },
  } = useInstitutionDetailsLocked();
  const previousRegisteredAddress = useInstitutionDetailsPreviousRegisteredAddress();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const legalForm = form.watch('legalForm');
  const typeOfBusiness = form.watch('typeOfBusiness');
  const subTypesOfBusiness = typeOfBusiness ? BUSINESS_SUB_TYPES_BY_BUSINESS_TYPE[typeOfBusiness] : [];
  const isCompanyNameLocked = lockedCompany?.companyName !== undefined;
  const isRegisteredAddressLocked = lockedCompany?.registeredAddress !== undefined;
  const isTypeOfBusinessLocked = lockedCompany?.typeOfBusiness !== undefined;
  const isTaxIdLocked = lockedCompany?.taxId !== undefined;
  const isLeiNumberLocked = lockedCompany?.leiNumber !== undefined;

  const onSubmit = (data: InstitutionCompanyOutput, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    mutate(
      { company: institutionCompanyToInstitutionDetailsCompanyApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(isReturningToReview ? `${onboardingPath}/review` : `${onboardingPath}/business-profile`, { replace: true });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <div className="flex flex-row justify-between -md:justify-end">
        <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={onPrevStep} />
        <InstitutionGoToReviewButton onSubmit={() => form.handleSubmit((data) => onSubmit(data, { isReturningToReview: true }))()} />
      </div>
      <PageCard size="lg" bordered fixedFooter>
        <form name="institution-company" onSubmit={form.handleSubmit((data) => onSubmit(data))}>
          <PageCardContent className="pt-8 space-y-6">
            {lockedCompany ? <LockedAnswersNotice i18nKey="institutionDetails.update.locked" /> : null}
            <FormField
              control={form.control}
              name="companyName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.company.companyName')}*</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isCompanyNameLocked} placeholder={t('institutionDetails.company.placeholders.companyName')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="legalForm"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.company.legalForm.title')}*</FormLabel>
                  <Select defaultValue={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('institutionDetails.company.placeholders.legalForm')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {LEGAL_FORMS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {t(`institutionDetails.company.legalForm.options.${option}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {legalForm === LegalForm.OTHER ? (
              <FormField
                control={form.control}
                name="legalFormOther"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('institutionDetails.company.legalFormOther')}*</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('institutionDetails.company.placeholders.legalFormOther')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {previousRegisteredAddress ? (
              <p className="text-sm text-muted-foreground">{t('institutionDetails.update.previousRegisteredAddress', { registeredAddress: previousRegisteredAddress })}</p>
            ) : null}
            <InstitutionFieldsetAddress<InstitutionCompany> disabled={isRegisteredAddressLocked} name="registeredAddress" />
            <FormField
              control={form.control}
              name="registrationNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.company.registrationNumber')}*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('institutionDetails.company.placeholders.registrationNumber')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <InstitutionFieldsetDate<InstitutionCompany> legend={t('institutionDetails.company.dateOfIncorporation')} name="dateOfIncorporation" />
            <FormField
              name="typeOfBusiness"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.company.typeOfBusiness.title')}*</FormLabel>
                  <Select
                    defaultValue={field.value}
                    disabled={isTypeOfBusinessLocked}
                    onValueChange={(value) => {
                      field.onChange(value);
                      form.setValue('subTypeOfBusiness', undefined);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t('institutionDetails.company.placeholders.typeOfBusiness')} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {BUSINESS_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`institutionDetails.company.typeOfBusiness.options.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {subTypesOfBusiness.length ? (
              <FormField
                control={form.control}
                name="subTypeOfBusiness"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('institutionDetails.company.subTypeOfBusiness.title')}*</FormLabel>
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('institutionDetails.company.placeholders.subTypeOfBusiness')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {subTypesOfBusiness.map((subType) => (
                          <SelectItem key={subType} value={subType}>
                            {t(`institutionDetails.company.subTypeOfBusiness.options.${subType}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {typeOfBusiness === BusinessType.OTHER ? (
              <FormField
                control={form.control}
                name="typeOfBusinessOther"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('institutionDetails.company.typeOfBusinessOther')}*</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('institutionDetails.company.placeholders.typeOfBusinessOther')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <FormField
              control={form.control}
              name="taxId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.company.taxId')}*</FormLabel>
                  <FormControl>
                    <Input {...field} disabled={isTaxIdLocked} placeholder={t('institutionDetails.company.placeholders.taxId')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="leiNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.company.leiNumber.title')}</FormLabel>
                  <FormDescription>{t('institutionDetails.company.leiNumber.description')}</FormDescription>
                  <FormControl>
                    <Input {...field} disabled={isLeiNumberLocked} placeholder={t('institutionDetails.company.placeholders.leiNumber')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('institutionDetails.company.website')}</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder={t('institutionDetails.company.placeholders.website')} inputMode="url" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </PageCardContent>
          <PageCardFooter>
            <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
              <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
              {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
            </Button>
          </PageCardFooter>
        </form>
      </PageCard>
    </Form>
  );
};
InstitutionCompanyFormForm.displayName = 'InstitutionCompanyFormForm';

const InstitutionCompanyForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { locked } = useInstitutionDetailsLocked();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'company');
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const handlePrevStep = useCallback(() => {
    navigate(`${onboardingPath}/branch-operations`, { replace: true });
  }, [navigate, onboardingPath]);

  if (isLoading) return <LoadingCard />;

  // Locked answers win, as they do when the draft is saved
  const defaultValues = {
    ...(institutionDetailsAnswers?.company ? institutionDetailsCompanyApiToInstitutionCompany(institutionDetailsAnswers.company) : {}),
    ...institutionDetailsLockedCompanyToInstitutionCompany(locked.company ?? {}),
  };

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.company.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.company.description')}</PageHeadingDescription>
      </PageHeading>
      <InstitutionCompanyFormForm defaultValues={defaultValues} onPrevStep={handlePrevStep} />
    </section>
  );
};
InstitutionCompanyForm.displayName = 'InstitutionCompanyForm';

export default InstitutionCompanyForm;
